/**
 * Shared hook that fetches the provider onboarding (personality start) completion status.
 *
 * Consolidates the duplicate API calls that were previously made independently
 * by ProviderOnboardingBanner, HamburgerMenu, and PersonalityStartModal.
 *
 * Returns `{ completed, oriented, loading }` where `completed` is `null`
 * until fetched, `true` if the wizard has been finished (or the practitioner
 * is already active), and `oriented` is `true` only when the provider has
 * actually seen the onboarding interview — an active practitioner who was
 * configured by the clinic still needs the first-login walkthrough.
 */
import { useState, useEffect } from 'react';
import { useApiGetAuth } from '../../hooks/useApiQuery';
import { usePermissions } from '../../hooks/usePermissions';

interface ProviderOnboardingStatusResult {
  completed: boolean | null;
  oriented: boolean | null;
  loading: boolean;
}

export function useProviderOnboardingStatus(
  user:
    | {
        id?: string | number;
        uuid?: string | number;
        organization_id?: string | number;
        roles?: string[];
        demo_mode?: boolean;
        character_type?: string;
      }
    | null
    | false,
): ProviderOnboardingStatusResult {
  const [completed, setCompleted] = useState<boolean | null>(null);
  const [oriented, setOriented] = useState<boolean | null>(null);
  const [loading, setLoading] = useState(true);
  const { hasPermission, loading: permissionsLoading } = usePermissions();

  const authUser = user && typeof user === 'object' ? user : null;

  // Demo users simulate a normal clinic day — no setup prompts.
  const isDemo = authUser?.demo_mode === true;

  // Use RBAC permission (notes.create) instead of hardcoded role strings.
  // This covers provider, supervisor, clinical_director, medical_assistant,
  // and supervised_provider — any role that creates clinical notes.
  const isProvider = !permissionsLoading && hasPermission('notes.create');

  // Only providers need PersonalityStart — admin-only users have their own
  // onboarding path and isProvider is already false for them.  Users with both
  // admin + provider roles (e.g., clinical_director) have isProvider true
  // and correctly get PersonalityStart.
  const shouldFetch = !isDemo && isProvider;

  // Scoped per user — the personality-start status is account-specific, and a
  // shared browser session must never serve the previous account's status to
  // the next user who logs in.
  const { data, isLoading: queryLoading } = useApiGetAuth(
    shouldFetch
      ? ['therapist-dashboard', 'personality-start', authUser?.uuid ?? authUser?.id]
      : null,
    '/api/therapist_dashboard/personality-start',
  );

  const userKey = authUser?.uuid ?? authUser?.id;

  // Reset on account switch — the previous user's status must not greet or
  // suppress the greeting for the next user in the same browser session.
  useEffect(() => {
    setCompleted(null);
    setOriented(null);
  }, [userKey]);

  useEffect(() => {
    if (data) {
      setCompleted(data.completed === true);
      setOriented(data.oriented === true);
    }
  }, [data]);

  useEffect(() => {
    if (!shouldFetch) setLoading(false);
    else setLoading(queryLoading);
  }, [shouldFetch, queryLoading]);

  return { completed, oriented, loading };
}
